import { Injectable, Optional, Inject } from '@angular/core';
import { queryShadowElement } from '@client/app/utils/query-shadow-element.util';
import { IS_MICROFRONTEND, OVERLAY_ROOT_TOKEN } from '@client/mfe/tokens/mfe-tokens';

/**
 * SmartScrollService - Handles smooth scrolling to page sections with MFE support.
 *
 * ## Overview
 * This service calculates scroll positions for smooth scrolling to anchor elements.
 * It supports both standalone mode and MFE (Micro-Frontend) mode where the app runs
 * inside a shadow DOM within Command Center.
 *
 * ## MFE Mode Considerations
 *
 * When the app runs as an MFE inside Command Center's shadow DOM, the coordinate
 * system for scroll calculations differs from standalone mode:
 *
 * ### The Problem
 * - `cumulativeOffset()` calculates positions relative to the document origin
 * - In MFE mode, the scroll container is inside a shadow DOM
 * - The shadow host (`<webgov-app>`) may have a non-zero offset from the viewport
 * - This causes scroll positions to be miscalculated by the mfe-root offset amount
 *
 * ### The Solution
 * In MFE mode, we subtract the mfe-root element's `getBoundingClientRect().top`
 * from the calculated scroll position. This accounts for the shadow host's
 * viewport offset, ensuring scroll positions are relative to the MFE container
 * rather than the document origin.
 *
 * ### Coordinate System
 *
 * Standalone Mode:
 * ```
 * scrollPosition = cumulativeOffset(target) - headerOffset
 * ```
 *
 * MFE Mode (mfeRoot.top > 0):
 * ```
 * scrollPosition = cumulativeOffset(target) - mfeRootOffset - headerOffset
 * ```
 *
 * MFE Mode (mfeRoot.top === 0):
 * ```
 * scrollPosition = cumulativeOffset(target) - headerOffset  // Same as standalone
 * ```
 *
 * ## Dependencies
 *
 * ### Standalone Mode
 * - Works without any MFE tokens
 * - Uses standard document-relative offset calculations
 *
 * ### MFE Mode
 * - Requires `IS_MICROFRONTEND` injection token set to `true`
 * - Requires `OVERLAY_ROOT_TOKEN` injection token pointing to `<mfe-root>` element
 * - Tokens are provided in `webgov-element.ts` during MFE bootstrap
 *
 * ## Usage Example
 *
 * ```typescript
 * // In a component with scroll navigation
 * scrollToSection(sectionId: string) {
 *     const container = queryShadowElement('.mon-dialog-body-with-footer', document.body);
 *     if (container) {
 *         // 120px offset accounts for sticky dialog header
 *         this.smartScroll.scrollTo(container, sectionId, 120);
 *     }
 * }
 * ```
 *
 * ## Testing
 *
 * See `smart-scroll.service.test.ts` for comprehensive test coverage including:
 * - Standalone mode scroll calculations
 * - MFE mode with various mfe-root offsets
 * - Edge cases (zero offset, negative values, nested containers)
 * - Token injection scenarios
 *
 * @see {@link https://github.com/monsido/wiki/mfe-scroll-behavior|MFE Scroll Behavior Documentation}
 */
@Injectable({
    providedIn: 'root',
})
export class SmartScrollService {
    private mfeRoot: HTMLElement | null = null;
    private isMicrofrontend = false;

    /**
     * Creates an instance of SmartScrollService.
     *
     * @param isMicrofrontend - Injection token indicating MFE mode.
     *                        Set to `true` when running inside Command Center.
     *                        Uses `@Optional()` since it's only available in MFE builds.
     *
     * @param overlayRoot - Injection token providing reference to `<mfe-root>` element.
     *                    Used to calculate the MFE container's viewport offset.
     *                    Uses `@Optional()` since it's only available in MFE builds.
     *
     * @remarks
     * Both tokens are injected with `@Optional()` decorator because they are only
     * provided when the app runs as an MFE inside Command Center. In standalone mode,
     * these tokens are not available, and the service falls back to standard
     * document-relative scroll calculations.
     *
     * The tokens are provided by the `webgov-element.ts` bootstrap code:
     * ```typescript
     * platformBrowserDynamic([
     *     { provide: IS_MICROFRONTEND, useValue: true },
     *     { provide: OVERLAY_ROOT_TOKEN, useValue: mfeRoot },
     * ])
     * ```
     */
    constructor (
        @Optional() @Inject(IS_MICROFRONTEND) isMicrofrontend: boolean | null,
        @Optional() @Inject(OVERLAY_ROOT_TOKEN) overlayRoot: HTMLElement | null,
    ) {
        this.isMicrofrontend = isMicrofrontend ?? false;
        this.mfeRoot = overlayRoot;
    }

    /**
     * Scrolls the container element to bring the target section into view.
     *
     * @param containerElement - The scrollable container element (e.g., `.mon-dialog-body-with-footer`)
     * @param sectionID - The ID of the target element to scroll to (without the `#` prefix)
     * @param offsetTop - Optional offset to subtract from the scroll position.
     *                   Typically used to account for sticky headers.
     *                   For dialog headers, use 120px (covers the 116px header + 4px padding).
     *
     * @remarks
     * The scroll operation is wrapped in `setTimeout` to ensure the DOM is fully
     * rendered before calculating positions. This is particularly important after
     * view updates or when dealing with dynamically loaded content.
     *
     * Uses `queryShadowElement` utility to find the target element, which traverses
     * shadow DOM boundaries. This is essential for MFE mode where elements may be
     * nested inside the shadow root.
     *
     * @example
     * ```typescript
     * // Scroll to 'features' section with 120px header offset
     * smartScroll.scrollTo(container, 'form_domain_features', 120);
     *
     * // Scroll with no offset
     * smartScroll.scrollTo(container, 'section-id', 0);
     * ```
     */
    scrollTo (containerElement: Element, sectionID: string, offsetTop?: number): void {
        setTimeout(() => {
            // document.getElementById cannot find elements inside shadow DOM (MFE).
            // queryShadowElement traverses shadow roots to find the target.
            const target = queryShadowElement('#' + sectionID, document.body) as HTMLElement | null;
            offsetTop = offsetTop || 0;
            if (target) {
                const targetPosition = this.calculateScrollPosition(target, offsetTop);
                containerElement.scroll({
                    top: targetPosition,
                    behavior: 'smooth',
                });
            }
        });
    }

    /**
     * Calculates the scroll position for a target element.
     *
     * In MFE mode, this method adjusts for the MFE container's offset from the viewport
     * to ensure accurate scrolling within the shadow DOM context.
     *
     * ## Calculation Logic
     *
     * 1. Calculate the target's cumulative offset from document origin using
     *    `cumulativeOffset()` which traverses up the `offsetParent` chain.
     *
     * 2. If in MFE mode and mfeRoot exists:
     *    - Get the mfe-root element's bounding rect
     *    - If mfeRootRect.top !== 0 (shadow host has viewport offset):
     *      - Subtract mfeRootRect.top from cumulative offset
     *      - This converts document-relative position to MFE-relative position
     *
     * 3. Subtract the caller's offsetTop (e.g., for sticky headers)
     *
     * ## Why This Matters
     *
     * In MFE mode, the DOM structure is:
     * ```
     * document (CC host)
     * └── <webgov-app> (Shadow Host, offset from viewport)
     *     └── Shadow Root
     *         └── <mfe-root> (MFE Container)
     *             └── .mon-dialog-body-with-footer (Scroll Container)
     *                 └── #target-section (Target)
     * ```
     *
     * `cumulativeOffset()` calculates: target.offsetTop + ... up to document origin
     * But the scroll container is inside shadow DOM, so we need to subtract
     * the shadow host's offset from viewport to get the correct position.
     *
     * @param target - The target HTMLElement to scroll to
     * @param offsetTop - Additional offset to subtract (e.g., sticky header height)
     * @returns The calculated scroll position in pixels
     *
     * @private
     */
    private calculateScrollPosition (
        target: HTMLElement,
        offsetTop: number,
    ): number {
        const cumulativeOffset = this.cumulativeOffset(target).top;

        // In MFE mode, subtract the MFE container's offset from viewport
        // because cumulativeOffset calculates relative to the document,
        // but the scroll container is inside the shadow DOM
        if (this.isMicrofrontend && this.mfeRoot) {
            const mfeRootRect = this.mfeRoot.getBoundingClientRect();
            // Only adjust if mfe-root has a non-zero offset from the viewport.
            // When mfeRootRect.top === 0, the shadow host is at the viewport origin,
            // so no adjustment is needed (same coordinate system as standalone).
            if (mfeRootRect.top !== 0) {
                return cumulativeOffset - mfeRootRect.top - offsetTop;
            }
        }

        return cumulativeOffset - offsetTop;
    }

    /**
     * Calculates the cumulative offset of an element relative to the document origin.
     *
     * Traverses up the `offsetParent` chain, summing `offsetTop` and `offsetLeft`
     * values until reaching the root. In MFE mode, this stops at the shadow boundary
     * because shadow DOM creates a new containing block.
     *
     * @param element - The element to calculate offset for
     * @returns Object with `top` and `left` cumulative offsets in pixels
     *
     * @remarks
     * The `offsetParent` chain:
     * - For positioned elements (position: relative/absolute/fixed): nearest positioned ancestor
     * - For non-positioned elements: nearest table-cell or root
     * - Stops at shadow DOM boundary (shadow host becomes offsetParent boundary)
     *
     * @private
     */
    private cumulativeOffset (element: Element): { top: number; left: number } {
        let top = 0;
        let left = 0;
        let currentElement = element as HTMLElement;

        while (currentElement) {
            top += currentElement.offsetTop || 0;
            left += currentElement.offsetLeft || 0;
            currentElement = currentElement.offsetParent as HTMLElement;
        }

        return {
            top: top,
            left: left,
        };
    }
}
